import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from './useAdminApi';

/** /admin/reviews — "how did we do?" emails after a service visit. */

export interface ReviewLinks {
  google: string;
  yelp: string;
  facebook: string;
}

export interface ReviewSettings {
  enabled: boolean;
  enabled_at: string | null;
  low_score_threshold: number;
  throttle_days: number;
  links: ReviewLinks;
}

export interface ReviewRequestRow {
  id: string;
  sf_job_id: string;
  sf_job_number: string | null;
  customer_name: string | null;
  location_name: string | null;
  job_status: string | null;
  job_category: string | null;
  recipients: string[] | null;
  source: 'auto' | 'manual';
  status: 'claimed' | 'sent' | 'failed' | 'skipped' | 'rated';
  skip_reason: string | null;
  sent_at: string | null;
  error: string | null;
  sent_by: string | null;
  rating: number | null;
  comment: string | null;
  rated_at: string | null;
  created_at: string;
}

interface ReviewsPayload {
  ok: true;
  settings: ReviewSettings;
  rows: ReviewRequestRow[];
  totals: { sent: number; rated: number; average: number | null; low: number };
}

export interface ReviewPassResult {
  ok: true;
  enabled: boolean;
  paused?: boolean;
  dry_run: boolean;
  cutoff: string | null;
  seen: number;
  sent: number;
  failed: number;
  skipped: Array<{ job: string; reason: string }>;
  would_send: Array<{ job: string; customer: string | null; to: string[]; via: string }>;
  budget_hit: boolean;
}

const KEY = ['admin', 'review-requests'];

export function useReviewRequests() {
  return useQuery({ queryKey: KEY, queryFn: () => adminFetch<ReviewsPayload>('admin-review-requests') });
}

function post<T>(body: Record<string, unknown>) {
  return adminFetch<T>('admin-review-requests', { method: 'POST', body: JSON.stringify(body) });
}

export function useSaveReviewSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<ReviewSettings>) => post<{ ok: true; settings: ReviewSettings }>({ action: 'settings', ...patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSendReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { job: string; to?: string; resend?: boolean }) =>
      post<{ ok: true; id: string; to: string[] }>({ action: 'send', ...v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRunReviewPass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) => post<ReviewPassResult>({ action: 'run', dry_run: dryRun }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
